import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getEntryApi } from '@/api/entries';
import Loading from '@/components/Shared/Loading';
import {
  EntryDraftNotice,
  useEntryDraftAutosave
} from '@/features/entry-drafts';
import { EntryEdit } from '@/scenes/Blog/EntryEdit/EntryEdit';
import {
  EntryEditProvider,
  useEntryEditContext
} from '@/scenes/Blog/EntryEdit/EntryEditContext';
import { PreviewEntry } from '@/scenes/Blog/EntryEdit/PreviewEntry';
import { publishEntry } from '@/store/entries/thunks/publishEntry';
import { removeEditedEntry } from '@/store/entries/thunks/removeEditedEntry';
import {
  useActiveProfile,
  useTokenState
} from '@/store/localStorage/useTokenState';
import { selectActiveProfileAugmented } from '@/store/user/selectors/selectActiveProfileAugmented';

function EntryEditView({
  hotUpdateTagList,
  unpinEntryIfPinned,
  pinEntry,
  blog
}) {
  const { eid, slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeProfileId } = useActiveProfile();
  const [token] = useTokenState();
  const profile = useSelector(state =>
    selectActiveProfileAugmented(state, { activeProfileId })
  );
  const [authorProfile, setAuthorProfile] = useState(profile);

  useEffect(() => {
    if (eid && eid !== '0') {
      document.title = `Редактирование записи - ${blog.blogTitle} - Дыбр`;
    } else {
      document.title = `Новая запись - ${blog.blogTitle} - Дыбр`;
    }
  }, [blog.blogTitle, eid]);

  const {
    editorApi,
    loading,
    setLoading,
    submitting,
    setSubmitting,
    error,
    setError,
    preview,
    entryContent,
    setEntryContent,
    entryTitle,
    setEntryTitle,
    tags,
    setTags,
    entrySettings,
    setEntrySettings,
    isPinned,
    setIsPinned,
    updatePublishAt
  } = useEntryEditContext();

  function fillFields(fields) {
    setEntryTitle(fields.title);
    setTags(fields.tags);
    // through the editor, so that Ctrl+Z brings the previous text back
    const applied = editorApi.current?.replaceContent(fields.content);
    // what the editor holds, it may have normalized the html. The state must
    // match it, or the editor would reset its undo history to follow the state.
    // Without an editor (loading, preview) it is the text as it is.
    setEntryContent(applied ?? fields.content);

    return applied;
  }

  const { checkDraft, removeDraft, draftNotice } = useEntryDraftAutosave({
    entryId: eid,
    blog,
    fields: { title: entryTitle, content: entryContent, tags },
    applyFields: fillFields,
    enabled: !submitting
  });

  // a stored draft of this server version replaces it, see checkDraft
  function fillForm(loadedEntry) {
    fillFields(loadedEntry);
    checkDraft(loadedEntry);
  }

  useEffect(() => {
    async function loadEntry() {
      setError('');
      setLoading(true);

      const { data: entry, error } = await getEntryApi({
        entryId: eid,
        include: 'profile',
        token
      });

      if (entry === undefined || error) {
        setError(error ?? 'что-то пошло не так');
      } else {
        if (entry.profile.id !== profile.id && blog.id !== profile.id) {
          setError('чужую запись редактировать нельзя');
          setLoading(false);
          return;
        }

        setAuthorProfile(entry.profile);
        fillForm({
          title: entry.title ?? '',
          content: entry.content ?? '',
          tags: entry.tags ?? [],
          updatedAt: entry.updatedAt
        });
        setEntrySettings({
          feed: blog.settings.privacy.dybrfeed,
          ...(entry.settings || {
            permissions: { access: [], comments: [] }
          })
        });
        setIsPinned(
          blog.settings.pinnedEntries &&
            blog.settings.pinnedEntries.includes(eid)
        );
      }

      setLoading(false);
    }

    if (eid && eid !== '0') {
      loadEntry();
    } else {
      fillForm({ title: '', content: '', tags: [] });
      setEntrySettings({
        permissions: { access: [], comments: [] },
        feed: blog.settings.privacy.dybrfeed
      });
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [eid, profile, token]);

  const handlePublishEntry = async content => {
    setSubmitting(true);

    const requestFields = {
      title: entryTitle,
      content,
      tags,
      state: 'published',
      // bogolt asked not to send default blog's value here
      settings: {
        ...entrySettings,
        feed:
          entrySettings.feed === blog.settings.privacy.dybrfeed
            ? undefined
            : entrySettings.feed
      }
    };

    const isPublished = await dispatch(
      publishEntry({
        entryId: eid,
        blogSlug: slug,
        targetBlog: blog,
        tags,
        hotUpdateTagList,
        isPinned,
        unpinEntryIfPinned,
        pinEntry,
        requestFields,
        updatePublishAt,
        removeEntryDraft: removeDraft,
        token,
        navigate
      })
    );

    // the form stays open: it can be published again, and its draft is saved
    // again, including the changes whose save the publish dropped
    if (!isPublished) {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = () =>
    dispatch(
      removeEditedEntry({
        entryId: eid,
        blog,
        token,
        removeEntryDraft: removeDraft,
        navigate
      })
    );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div className="blog-info-block">{error}</div>;
  }

  return preview ? (
    <PreviewEntry
      onPublish={handlePublishEntry}
      onDelete={handleDeleteEntry}
      eid={eid}
    />
  ) : (
    <EntryEdit
      eid={eid}
      authorProfile={authorProfile}
      onPublish={handlePublishEntry}
      onDelete={handleDeleteEntry}
      blog={blog}
      draftNotice={<EntryDraftNotice {...draftNotice} />}
    />
  );
}

export function EntryEditWrapper(props) {
  const { eid } = useParams();

  // a fresh form per entry, so the fields of one entry never reach another
  return (
    <EntryEditProvider key={eid}>
      <EntryEditView {...props} />
    </EntryEditProvider>
  );
}

export function BlogEntryEditContainer(props) {
  // This wrapper is for compatibility with previous usage as a container
  // It simply passes all props to EntryEditWrapper
  return <EntryEditWrapper {...props} />;
}
